import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { RatingDto } from '@/Domain/api/Conversation/dto/Rating.dto';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  ConversationRatingQuestionCodeEnum,
  ConversationRatingResponseCodeEnum,
} from '@/Domain/enum/ConversationRating.enum';
import { ConversationHelper } from '@/Domain/helper/Conversation.helper';

type Props = {
  rating: RatingDto[] | undefined;
  hasRating: boolean;
};

export const RatingPopover = ({ rating, hasRating }: Props) => {
  const { t } = useTranslation('inbox_v2');

  if (!rating || !hasRating) {
    return <p className="text-xs text-muted-foreground">{t('rating.empty')}</p>;
  }

  const helpfulQuestion = ConversationHelper.getRatingByQuestionCode(
    rating,
    ConversationRatingQuestionCodeEnum.HelpfulThumbs,
  );

  const experienceQuestion = ConversationHelper.getRatingByQuestionCode(
    rating,
    ConversationRatingQuestionCodeEnum.ExperienceThumbs,
  );

  const whyNotHelpfulQuestion = ConversationHelper.getRatingByQuestionCode(
    rating,
    ConversationRatingQuestionCodeEnum.HelpfulWhy,
  );

  const feedback = ConversationHelper.getRatingByQuestionCode(
    rating,
    ConversationRatingQuestionCodeEnum.Feedback,
  );

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-foreground font-medium text-sm">{t('rating.title')}</h3>
      <section className="flex justify-between items-center gap-2">
        <p className="text-muted-foreground font-medium text-xs">
          {helpfulQuestion?.question_value}
        </p>
        <div className="bg-muted p-1.5 rounded-lg">
          <Icon
            icon={ConversationHelper.getRatingByResponseCode(helpfulQuestion?.response_code)}
            className="text-foreground-subtle"
          />
        </div>
      </section>

      {experienceQuestion && (
        <section className="flex justify-between items-center gap-2">
          <p className="text-muted-foreground font-medium  text-xs">
            {experienceQuestion?.question_value}
          </p>
          <div className="bg-muted p-1.5 rounded-lg">
            <Icon
              icon={ConversationHelper.getRatingByResponseCode(experienceQuestion?.response_code)}
              className="text-foreground-subtle"
            />
          </div>
        </section>
      )}

      {whyNotHelpfulQuestion && (
        <>
          <section className="flex justify-between items-center gap-2">
            <p className="text-muted-foreground font-medium text-xs">
              {whyNotHelpfulQuestion?.question_value}
            </p>
            <div className="bg-muted p-1.5 rounded-lg font-medium text-xxs tracking-wide">
              {ConversationHelper.getRatingSelectedField(
                whyNotHelpfulQuestion.select_fields,
                whyNotHelpfulQuestion.response_code,
              )}
            </div>
          </section>
          {whyNotHelpfulQuestion.response_code === ConversationRatingResponseCodeEnum.Other && (
            <>
              <Separator />
              <p className="text-muted-foreground text-xs">
                {whyNotHelpfulQuestion.response_value}
              </p>
            </>
          )}
        </>
      )}

      {feedback?.response_value && (
        <>
          <Separator />
          <p className="text-muted-foreground font-medium text-xs">{t('rating.feedback')}</p>
          <p className="text-muted-foreground text-xs">{feedback?.response_value}</p>
        </>
      )}
    </div>
  );
};
